// Hook count de mensajes no leídos (sin filtro por paciente_id).
//
// Sprint 3 B6: usado por KineSidebar (badge global "Mensajes") y
// PacienteBottomNav (badge en "Mensajes" item).
//
// RLS filtra automáticamente: kine ve los de sus pacientes, paciente
// ve los suyos. NO requiere kine_id/paciente_id como argumento.
//
// El "otro sender" depende del viewer:
//   - kine logueado → cuenta mensajes con sender='paciente' sin leer
//   - paciente logueado → cuenta mensajes con sender='kine' sin leer
// El viewer NO se infiere — el caller pasa `viewerRole` para que
// el filtro sea explícito.
//
// Polling 30s — más relajado que la conversación (15s) porque es
// solo un badge, no afecta UX activo.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../client";
import type { MensajeSender } from "./useMensajesConversacion";

const POLL_MS = 30_000;

export function useUnreadCount(viewerRole: MensajeSender | null): number {
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!viewerRole) {
      setCount(0);
      return;
    }
    const otroSender: MensajeSender =
      viewerRole === "kine" ? "paciente" : "kine";
    const { count: c, error } = await supabase
      .from("mensajes")
      .select("*", { count: "exact", head: true })
      .eq("sender", otroSender)
      .is("read_at", null);
    if (error) {
      // Silencioso — el badge no es crítico. Logueamos para debugging.
      console.warn("[useUnreadCount] fetch error", error);
      return;
    }
    setCount(c ?? 0);
  }, [viewerRole]);

  useEffect(() => {
    if (!viewerRole) return;
    void fetchCount();
    const intervalId = setInterval(() => {
      void fetchCount();
    }, POLL_MS);
    const onFocus = () => {
      void fetchCount();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [viewerRole, fetchCount]);

  return count;
}
